import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Card } from "../../../../components/ui/Card";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { Modal } from "../../../../components/ui/Modal";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import { RelativeTime } from "../../../../components/ui/RelativeTime";
import { showToast } from "../../../../utils/showToast";
import {
  deleteAiServiceAccount,
  getAiServiceAccount,
  isEndpointMissing,
  saveAiServiceAccount,
  testAiServiceAccount,
} from "./aiActionsApi";
import { aiErrorMessage, normalizeCaller } from "./aiErrorMessage";
import { CONTROL_CLS, PRIMARY_BTN_CLS, SECONDARY_BTN_CLS } from "./agentSections/primitives";

// Cuenta de servicio: el usuario de Beartrack con el que la IA ejecuta las
// acciones del caso (asignar repartidor, crear incidencias…). Sin ella la IA
// conversa pero no puede tocar nada; con ella hereda EXACTAMENTE los permisos
// de ese usuario, así que la tarjeta tiene que dejar claro qué se está
// habilitando antes de que alguien pegue una contraseña.
//
// SEGURIDAD: la contraseña vive solo en el estado local mientras se tipea. No
// se guarda, no se reenvía en un PUT posterior, no se loguea y no se vuelve a
// pintar — después de guardar el input se limpia y lo único que se muestra es
// lo que el backend devuelve enmascarado (email, token_last4, status).

/**
 * Estado de la cuenta como chip: "Configurada" / "Sin configurar" y, cuando el
 * backend lo informa, el estado real de la credencial. Un estado desconocido
 * cae a "Configurada" (el backend dijo que hay cuenta; el detalle no lo
 * conocemos) en vez de inventar una etiqueta.
 * @param {{configured?: boolean, status?: string}} account
 * @param {(key: string) => string} t
 * @returns {{label: string, tone: string}}
 */
function accountBadge(account, t) {
  if (!account?.configured) return { label: t("Sin configurar"), tone: "muted" };
  const status = account.status;
  if (status === "active") return { label: t("Activa"), tone: "success" };
  if (status === "revoked") return { label: t("Revocada"), tone: "neutral" };
  if (status === "invalid") return { label: t("Inválida"), tone: "error" };
  return { label: t("Configurada"), tone: "success" };
}

export default function ServiceAccountCard({ caller }) {
  const { t } = useTranslation();

  // loading → se está leyendo la cuenta; missing → la ruta no existe todavía
  // (404/405); error → falló de verdad y se ofrece reintentar.
  const [state, setState] = useState("loading");
  const [account, setAccount] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Permisos del llamador: viajan con el catálogo que ya carga el padre
  // (AiSettings). Sin `caller` (backend anterior) todo queda permitido, como
  // siempre: el backend decide y responde un 403 explicado.
  const canManage = normalizeCaller(caller).can_manage_service_account;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  // StrictMode monta → limpia → monta sobre el MISMO fiber: `alive` se re-fija
  // en el cuerpo del efecto para que la segunda carga no quede muda.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // `t` cambia de identidad cuando cambia el idioma de la interfaz: si entrara a
  // las deps del load, traducir la pantalla recargaría la cuenta y devolvería el
  // formulario al esqueleto, borrando el correo y la contraseña a medio tipear.
  // El mensaje de error se traduce con la instancia vigente al fallar.
  const tRef = useRef(t);
  tRef.current = t;

  // `silent` para las relecturas que siguen a una acción ya confirmada (probar
  // la conexión): volver a "loading" haría parpadear el formulario a esqueleto
  // en cada click, borrando lo que el usuario acaba de escribir.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setState("loading");
    setLoadError(null);
    try {
      const res = await getAiServiceAccount();
      if (!aliveRef.current) return;
      setAccount(res?.account || {});
      setState("ready");
    } catch (err) {
      if (!aliveRef.current) return;
      // La relectura es accesoria y NUNCA degrada lo que ya está en pantalla:
      // ni al aviso de "en construcción" (un 404 transitorio no borra una
      // cuenta que ya se mostró) ni a la pantalla de error. Se conserva lo
      // último que dijo el backend y se avisa por toast.
      if (silent) {
        showToast(
          aiErrorMessage(err, tRef.current) ||
            tRef.current("No se pudo cargar la cuenta de servicio."),
          "warning",
        );
      } else if (isEndpointMissing(err)) {
        setState("missing");
      } else {
        setLoadError(
          aiErrorMessage(err, tRef.current) || tRef.current("No se pudo cargar la cuenta de servicio."),
        );
        setState("error");
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = useCallback(
    async (event) => {
      event.preventDefault();
      if (saving) return;
      const trimmed = email.trim();
      if (!trimmed || !password) {
        setFormError(t("Escribe el correo y la contraseña de la cuenta."));
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        setFormError(t("Ese correo no parece válido."));
        return;
      }
      setFormError(null);
      setSaving(true);
      try {
        const res = await saveAiServiceAccount({ email: trimmed, password });
        if (!aliveRef.current) return;
        // Sin `account` en la respuesta solo se afirma lo que sí sabemos
        // (quedó configurada con este correo): el estado real lo dice el
        // backend, no un "active" inventado acá.
        setAccount(res?.account || { configured: true, email: trimmed });
        // La contraseña se descarta en cuanto el backend la acepta: no queda en
        // el estado del componente ni vuelve a viajar en un guardado posterior.
        setPassword("");
        setState("ready");
        showToast(t("Cuenta de servicio guardada."), "success");
        // Relectura silenciosa: el backend puede completar lo que la respuesta
        // del POST no trae (estado real de la credencial, último uso). Silenciosa
        // para no devolver el formulario al esqueleto ni perder el foco.
        load({ silent: true });
      } catch (err) {
        if (!aliveRef.current) return;
        showToast(
          isEndpointMissing(err)
            ? t("La cuenta de servicio está en construcción.")
            : aiErrorMessage(err, t) || t("No se pudo guardar la cuenta de servicio."),
          "error",
        );
      } finally {
        if (aliveRef.current) setSaving(false);
      }
    },
    [email, load, password, saving, t],
  );

  const handleTest = useCallback(async () => {
    if (testing) return;
    setTesting(true);
    try {
      const res = await testAiServiceAccount();
      if (!aliveRef.current) return;
      showToast(
        res?.ok === false
          ? res?.error || t("La cuenta de servicio no pudo iniciar sesión.")
          : t("Conexión correcta."),
        res?.ok === false ? "error" : "success",
      );
      // El test puede dejar `last_error`/`last_used_at` nuevos: se relee para
      // que el estado en pantalla no quede mintiendo.
      load({ silent: true });
    } catch (err) {
      if (!aliveRef.current) return;
      showToast(
        isEndpointMissing(err)
          ? t("La cuenta de servicio está en construcción.")
          : aiErrorMessage(err, t) || t("No se pudo probar la cuenta de servicio."),
        "error",
      );
    } finally {
      if (aliveRef.current) setTesting(false);
    }
  }, [load, testing, t]);

  const handleRevoke = useCallback(async () => {
    if (revoking) return;
    setRevoking(true);
    try {
      await deleteAiServiceAccount();
      if (!aliveRef.current) return;
      setConfirmRevoke(false);
      setAccount({ configured: false });
      // La credencial revocada se va del formulario: dejarla tipeada (o el
      // correo anterior a la vista) invita a "guardar" algo que ya no existe.
      setEmail("");
      setPassword("");
      setFormError(null);
      showToast(t("Cuenta de servicio revocada."), "success");
    } catch (err) {
      if (!aliveRef.current) return;
      showToast(
        isEndpointMissing(err)
          ? t("La cuenta de servicio está en construcción.")
          : aiErrorMessage(err, t) || t("No se pudo revocar la cuenta de servicio."),
        "error",
      );
    } finally {
      if (aliveRef.current) setRevoking(false);
    }
  }, [revoking, t]);

  const badge = accountBadge(account, t);
  const configured = !!account?.configured;

  return (
    <Card padding="md">
      <div className="flex items-start gap-3">
        <span
          className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-bg-muted text-fg-muted shrink-0"
          aria-hidden="true"
        >
          <KeyRound className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xs uppercase tracking-wide font-semibold text-fg-muted">
              {t("Cuenta de servicio")}
            </h2>
            {state === "ready" && (
              <Badge tone={badge.tone} dot>
                {badge.label}
              </Badge>
            )}
          </div>
          <p className="text-[12px] md:text-[11px] text-fg-muted">
            {t(
              "La IA ejecuta acciones —asignar repartidor, crear incidencias— con esta cuenta de tu courier, con los mismos permisos que ese usuario.",
            )}{" "}
            {t("Solo lo que le habilites por agente, y siempre pidiendo confirmación en el chat.")}
          </p>
          {/* La credencial con la que la IA opera el courier no la configura
              cualquier rol: sin permiso la tarjeta queda de solo lectura y se
              dice por qué (es la explicación accesible de los campos
              deshabilitados). */}
          {!canManage && (
            <p id="ai-sa-readonly" className="text-[12px] md:text-[11px] text-fg-muted">
              {t("Solo un administrador puede configurar la cuenta de servicio.")}
            </p>
          )}
        </div>
      </div>

      {state === "loading" && (
        <div className="mt-4 space-y-2" aria-hidden="true">
          <Skeleton className="h-3 w-48 rounded" />
          <Skeleton className="h-11 md:h-8 w-full rounded-md" />
        </div>
      )}

      {state === "missing" && (
        <PendingBackendNotice className="mt-4">
          {t("La cuenta de servicio está en construcción.")}
        </PendingBackendNotice>
      )}

      {state === "error" && (
        <div className="mt-4 flex items-center gap-2 text-xs text-status-error" role="alert">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          <span className="min-w-0">{loadError}</span>
          <button
            type="button"
            onClick={load}
            className="h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
          >
            {t("Reintentar")}
          </button>
        </div>
      )}

      {state === "ready" && (
        <>
          {configured && (
            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2 text-[12px] md:text-[11px]">
              <div className="min-w-0">
                <dt className="text-fg-muted">{t("Cuenta")}</dt>
                <dd className="text-fg font-medium break-all">{account?.email || "—"}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">{t("Credencial")}</dt>
                <dd className="text-fg font-medium tabular-nums">
                  {account?.token_last4 ? `••••${account.token_last4}` : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-fg-muted">{t("Último uso")}</dt>
                <dd className="text-fg font-medium">
                  {account?.last_used_at ? (
                    <RelativeTime value={account.last_used_at} t={t} live />
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
            </dl>
          )}

          {configured && account?.last_error && (
            <div
              role="status"
              className="mt-3 flex items-start gap-2 rounded-lg bg-status-error-soft px-3 py-2 text-[12px] text-status-error"
            >
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
              <span className="min-w-0 break-words">{account.last_error}</span>
            </div>
          )}

          {/* La credencial se pide SIEMPRE completa: el backend no la devuelve,
              así que no hay forma de "editar" una guardada — se reemplaza. */}
          <form className="mt-4 space-y-3" onSubmit={handleSave} noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label htmlFor="ai-sa-email" className="text-xs font-medium text-fg">
                  {t("Correo de la cuenta")}
                </label>
                <input
                  id="ai-sa-email"
                  type="email"
                  // autoComplete off: el navegador ofrecería las credenciales del
                  // propio admin logueado, que no son las de la cuenta de servicio.
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={saving || !canManage}
                  aria-describedby={canManage ? undefined : "ai-sa-readonly"}
                  placeholder="ia@tucourier.cl"
                  className={CONTROL_CLS}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="ai-sa-password" className="text-xs font-medium text-fg">
                  {t("Contraseña")}
                </label>
                <input
                  id="ai-sa-password"
                  type="password"
                  // new-password: evita que el gestor de contraseñas pise la
                  // credencial guardada del admin con esta.
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={saving || !canManage}
                  aria-describedby={canManage ? undefined : "ai-sa-readonly"}
                  className={CONTROL_CLS}
                />
              </div>
            </div>

            {formError && (
              <p role="alert" className="text-xs text-status-error">
                {formError}
              </p>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="submit"
                disabled={saving || !canManage}
                aria-describedby={canManage ? undefined : "ai-sa-readonly"}
                className={PRIMARY_BTN_CLS}
              >
                {saving && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                )}
                {configured ? t("Reemplazar cuenta") : t("Guardar cuenta")}
              </button>
              <button
                type="button"
                onClick={handleTest}
                disabled={!configured || testing || !canManage}
                aria-describedby={canManage ? undefined : "ai-sa-readonly"}
                className={SECONDARY_BTN_CLS}
              >
                {testing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
                )}
                {t("Probar")}
              </button>
              {configured && (
                <button
                  type="button"
                  onClick={() => setConfirmRevoke(true)}
                  disabled={revoking || !canManage}
                  aria-describedby={canManage ? undefined : "ai-sa-readonly"}
                  className={cn(
                    SECONDARY_BTN_CLS,
                    "text-status-error border-status-error-soft hover:bg-status-error-soft",
                  )}
                >
                  {t("Revocar")}
                </button>
              )}
            </div>
          </form>
        </>
      )}

      <Modal
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        title={t("Revocar cuenta de servicio")}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRevoke(false)}>
              {t("Cancelar")}
            </Button>
            <Button variant="danger" onClick={handleRevoke} loading={revoking}>
              {t("Revocar")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg">
          {t(
            "La IA dejará de poder ejecutar acciones en los casos hasta que configures una cuenta nueva.",
          )}
        </p>
        <p className="mt-2 text-sm text-fg-muted">
          {t("Las conversaciones no se ven afectadas: el asistente sigue respondiendo.")}
        </p>
      </Modal>
    </Card>
  );
}
